import { z } from "zod";

import type { ClientServiceProfileFieldsFragment } from "../../__generated__/graphql";

export const BABY_SERVICE_ID = "17";

export const saveAsOptions = [
  { value: "Parent/Guardian", label: "Parent/Guardian" },
  { value: "Other", label: "Other" },
] as const;

type HealthConditionKey =
  | "fever"
  | "poorFeeding"
  | "breathingDifficulties"
  | "dizzinessFainting"
  | "allergicReactions"
  | "behavioralConcerns"
  | "specialCareNeeds";

export const healthConditionOptions: { key: HealthConditionKey; label: string }[] = [
  { key: "fever", label: "Fever or recent illness (cough, flu, HFMD, etc.)" },
  { key: "poorFeeding", label: "Poor feeding or dehydration" },
  { key: "breathingDifficulties", label: "Breathing difficulties or chest issues" },
  { key: "dizzinessFainting", label: "Dizziness / fainting episodes" },
  { key: "allergicReactions", label: "Allergic reactions (food, medication, environment)" },
  { key: "behavioralConcerns", label: "Behavioral concerns (aggression, self-harm tendencies)" },
  { key: "specialCareNeeds", label: "Special care needs (tube feeding, medical devices)" },
];

type TaskKey =
  | "feeding"
  | "diapering"
  | "bathing"
  | "napBedtime"
  | "playEngagement"
  | "lightHousehold";

export const babysitterTasksOptions: { key: TaskKey; label: string; caption?: string }[] = [
  {
    key: "feeding",
    label: "Feeding (milk, solid food, snacks – client provides ingredients if needed)",
    caption:
      "Babysitter handles feeding only. Parents must provide food/ingredients. Not responsible for special diets beyond instructions.",
  },
  { key: "diapering", label: "Diapering & Toileting" },
  { key: "bathing", label: "Bathing & Hygiene (supervised bath)" },
  { key: "napBedtime", label: "Nap & Bedtime Routines" },
  {
    key: "playEngagement",
    label: "Play & Engagement Activities (e.g., reading, coloring, toys, music)",
    caption:
      "Babysitter can supervise and encourage reading/play but not provide formal teaching or tutoring.",
  },
  {
    key: "lightHousehold",
    label: "Light Household Help (cleaning up after child, toys, minor tidying)",
    caption:
      "Babysitter can tidy toys or small messes related to childcare only. No full house cleaning.",
  },
];

const HEALTH_CONDITION_KEYS = healthConditionOptions.map((o) => o.key);
const TASK_KEYS = babysitterTasksOptions.map((o) => o.key);

const DETAIL_REQUIRED_MESSAGE =
  "To proceed, please provide the required details (minimum 5 characters)";
const TASK_REQUIRED_MESSAGE = "Please select at least one task";

const healthConditionSchema = z.object({
  fever: z.boolean().default(false),
  poorFeeding: z.boolean().default(false),
  breathingDifficulties: z.boolean().default(false),
  dizzinessFainting: z.boolean().default(false),
  allergicReactions: z.boolean().default(false),
  behavioralConcerns: z.boolean().default(false),
  specialCareNeeds: z.boolean().default(false),
});

const babysitterTasksSchema = z.object({
  feeding: z.boolean().default(false),
  diapering: z.boolean().default(false),
  bathing: z.boolean().default(false),
  napBedtime: z.boolean().default(false),
  playEngagement: z.boolean().default(false),
  lightHousehold: z.boolean().default(false),
});

export const babyProfileSchema = z
  .object({
    saveAs: z.enum(["Parent/Guardian", "Other"]).default("Parent/Guardian"),
    name: z.string().min(1, "Name is required"),
    age: z
      .string()
      .min(1, "Age is required")
      .refine((val) => /^\d+\s*[a-zA-Z]+/.test(val), {
        message: "Age must be a number followed by a unit (e.g., '4 weeks', '2 months')",
      }),
    healthConditions: healthConditionSchema,
    healthConditionsDetails: z.string().optional(),
    babysitter_tasks: babysitterTasksSchema,
    routineDetails: z.string().min(1, "This information is required"),
    primary: z.boolean().default(true),
  })
  .superRefine((val, ctx) => {
    const hasHealthCondition = Object.values(val.healthConditions).some((c) => c === true);
    if (
      hasHealthCondition &&
      (!val.healthConditionsDetails || val.healthConditionsDetails.trim().length < 5)
    ) {
      ctx.addIssue({
        path: ["healthConditionsDetails"],
        code: z.ZodIssueCode.custom,
        message: DETAIL_REQUIRED_MESSAGE,
      });
    }

    const hasTaskSelected = Object.values(val.babysitter_tasks).some((t) => t === true);
    if (!hasTaskSelected) {
      ctx.addIssue({
        path: ["babysitter_tasks"],
        code: z.ZodIssueCode.custom,
        message: TASK_REQUIRED_MESSAGE,
      });
    }
  });

export type BabyProfileFormValues = z.input<typeof babyProfileSchema>;

export const emptyBabyValues: BabyProfileFormValues = {
  saveAs: "Parent/Guardian",
  name: "",
  age: "",
  healthConditions: {
    fever: false,
    poorFeeding: false,
    breathingDifficulties: false,
    dizzinessFainting: false,
    allergicReactions: false,
    behavioralConcerns: false,
    specialCareNeeds: false,
  },
  healthConditionsDetails: "",
  babysitter_tasks: {
    feeding: false,
    diapering: false,
    bathing: false,
    napBedtime: false,
    playEngagement: false,
    lightHousehold: false,
  },
  routineDetails: "",
  primary: true,
};

/**
 * Form values -> profileAttributes JSON for the mutation.
 * name/primary are top-level mutation inputs, not attributes.
 * `babysitter_tasks` collapses the checkbox object to the selected keys;
 * `healthConditions` is stored as the object as-is (parity with the legacy form).
 */
export function toBabyAttributes(values: BabyProfileFormValues) {
  const { name: _name, primary: _primary, babysitter_tasks, ...rest } = values;
  return {
    ...rest,
    babysitter_tasks: TASK_KEYS.filter((key) => babysitter_tasks?.[key]),
  };
}

/** Stored profile -> form values (tasks string[] -> object; conditions object as-is). */
export function fromBabyProfile(
  profile: ClientServiceProfileFieldsFragment,
): BabyProfileFormValues {
  const attrs = (profile.profileAttributes ?? {}) as Record<string, unknown>;
  const storedConditions = (attrs["healthConditions"] as Record<string, boolean> | undefined) ?? {};
  const storedTasks = (attrs["babysitter_tasks"] as string[] | undefined) ?? [];

  return {
    saveAs: (attrs["saveAs"] as "Parent/Guardian" | "Other") ?? "Parent/Guardian",
    name: profile.name,
    age: (attrs["age"] as string) ?? "",
    healthConditions: Object.fromEntries(
      HEALTH_CONDITION_KEYS.map((key) => [key, storedConditions[key] ?? false]),
    ) as BabyProfileFormValues["healthConditions"],
    healthConditionsDetails: (attrs["healthConditionsDetails"] as string) ?? "",
    babysitter_tasks: Object.fromEntries(
      TASK_KEYS.map((key) => [key, storedTasks.includes(key)]),
    ) as BabyProfileFormValues["babysitter_tasks"],
    routineDetails: (attrs["routineDetails"] as string) ?? "",
    primary: profile.primary,
  };
}
